import { useSignal } from "@preact/signals";
import { inputs, settings, calculationResult, updateInput } from "../state/store";
import { SOCIAL_COSTS_RATE } from "../logic/calculator";
import { SaveSplitButton } from "./SaveSplitButton";

const card = "bg-lw-surface rounded-2xl border border-lw-border shadow-sm p-6";
const label = "block text-sm font-medium text-lw-text mb-2";
const range = "w-full h-11 accent-lw-blue";
const row = "flex justify-between mb-3";

export const CalculationPage = () => {
  const showDetails = useSignal(false);
  const i = inputs.value;
  const r = calculationResult.value;

  const modeBtn = (active: boolean, side: "l" | "r") =>
    `flex-1 px-4 py-2 text-sm font-medium border transition-colors ${side === "l" ? "rounded-l-full" : "rounded-r-full -ml-px"
    } ${active
      ? "bg-lw-blue-deep border-lw-blue-deep text-white"
      : "bg-lw-surface border-lw-blue-deep text-lw-blue-deep hover:bg-lw-blue/10"
    }`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className={card + " mb-0"}>
        <div className="relative flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-lw-navy">Car Details</h3>
          <SaveSplitButton />
        </div>

        <div className="flex w-full mb-6">
          <button
            type="button"
            className={modeBtn(i.inputMode === "FROM_PRICE", "l")}
            onClick={() => updateInput("inputMode", "FROM_PRICE")}
          >
            Calculate from Price
          </button>
          <button
            type="button"
            className={modeBtn(i.inputMode === "DIRECT_LEASE", "r")}
            onClick={() => updateInput("inputMode", "DIRECT_LEASE")}
          >
            Direct Lease Cost
          </button>
        </div>

        {i.inputMode === "FROM_PRICE" ? (
          <>
            <div className="mb-4">
              <label className={label}>
                Car Price: <strong>{i.carPrice.toLocaleString()} kr</strong>
              </label>
              <input
                type="range"
                className={range}
                min="300000"
                max="1000000"
                step="10000"
                value={i.carPrice}
                onInput={(e) => updateInput("carPrice", Number(e.currentTarget.value))}
              />
            </div>

            <div className="mb-4">
              <label className={label}>
                Planned Residue after 3 years: <strong>{i.residualValuePercent}%</strong>
              </label>
              <input
                type="range"
                className={range}
                min="30"
                max="50"
                step="5"
                value={i.residualValuePercent}
                onInput={(e) => updateInput("residualValuePercent", Number(e.currentTarget.value))}
              />
            </div>
          </>
        ) : (
          <div className="mb-4">
            <label className={label}>
              Monthly Lease Cost (excl VAT):{" "}
              <strong>{i.leaseCostExclVat.toLocaleString()} kr</strong>
            </label>
            <input
              type="range"
              className={range}
              min="1000"
              max="20000"
              step="100"
              value={i.leaseCostExclVat}
              onInput={(e) => updateInput("leaseCostExclVat", Number(e.currentTarget.value))}
            />
            <div className="mt-1 text-xs text-lw-text/60">
              Enter the monthly cost from your dealer quote, excluding VAT.
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className={label}>
            Car Benefit Value: <strong>{i.benefitValue.toLocaleString()} kr/month</strong>
          </label>
          <input
            type="range"
            className={range}
            min="0"
            max="10000"
            step="50"
            value={i.benefitValue}
            onInput={(e) => updateInput("benefitValue", Number(e.currentTarget.value))}
          />
        </div>

        <div className="mb-4">
          <label className={label}>
            Work Driving: <strong>{(i.workDrivingKm ?? 0).toLocaleString()} km</strong>
          </label>
          <input
            type="range"
            className={range}
            min="0"
            max="2500"
            step="50"
            value={i.workDrivingKm ?? 0}
            onInput={(e) => updateInput("workDrivingKm", Number(e.currentTarget.value))}
          />
        </div>

        <div className="mb-4">
          <label className={label}>
            Marginal Tax: <strong>{i.marginalTaxPercent}%</strong>
          </label>
          <input
            type="range"
            className={range}
            min="30"
            max="55"
            step="5"
            value={i.marginalTaxPercent}
            onInput={(e) => updateInput("marginalTaxPercent", Number(e.currentTarget.value))}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className={card + " bg-lw-bg"}>
          <h3 className="text-xl font-semibold text-lw-navy mb-6">Results</h3>

          <div className={row}>
            <span>Monthly Lease Cost (inc VAT):</span>
            <span className="font-bold">{Math.round(r.monthlyLeaseCost)} kr</span>
          </div>

          <div className={row + " text-lw-text/60"}>
            <span>Total Company Cost:</span>
            <span>{Math.round(r.totalCompanyCost)} kr</span>
          </div>

          <hr className="border-lw-border my-3" />

          <div className="flex justify-between mb-3 text-lg text-lw-navy">
            <span>Gross Salary Deduction:</span>
            <span className="font-bold">{Math.round(r.grossSalaryDeduction)} kr</span>
          </div>

          <div className="flex justify-between mb-3 text-lg text-lw-blue-deep border-t border-lw-border pt-3">
            <span>Net Cost to Employee:</span>
            <span className="font-bold">{Math.round(r.netCostToEmployee)} kr</span>
          </div>

          <div className="border-t border-lw-border pt-3 mt-3">
            <div className="flex justify-between text-base text-lw-blue-deep">
              <span>Fuel Compensation (Tax-free):</span>
              <span className="font-bold">{Math.round(r.totalFuelCompensation)} kr</span>
            </div>
          </div>

          <div className="bg-lw-bg border border-lw-border text-lw-text rounded-lg p-3 mt-4 text-sm">
            <div className="flex justify-between">
              <span>Post-tax Deduction:</span>
              <span>
                {Math.round(r.grossSalaryDeduction * (1 - i.marginalTaxPercent / 100))} kr
              </span>
            </div>
            <div className="flex justify-between">
              <span>Tax on Benefit:</span>
              <span>{Math.round(i.benefitValue * i.marginalTaxPercent / 100)} kr</span>
            </div>
          </div>

          <div className="border-t border-lw-border mt-4 pt-2">
            <button
              type="button"
              className="text-sm text-lw-text/60 hover:text-lw-text"
              onClick={() => (showDetails.value = !showDetails.value)}
              aria-expanded={showDetails.value}
            >
              {showDetails.value ? "Hide" : "Show"} calculation details{" "}
              <span>{showDetails.value ? "▲" : "▼"}</span>
            </button>

            {showDetails.value && (
              <div className="mt-2 text-sm text-lw-text/60">
                <div className="mb-4">
                  <div className="font-semibold mb-1 text-lw-text">
                    Company cost breakdown
                  </div>
                  <div className="flex justify-between">
                    <span>Lease expense (net + ½ VAT):</span>
                    <span>{Math.round(r.details.leaseExpense).toLocaleString()} kr</span>
                  </div>

                  {i.inputMode === "FROM_PRICE" && (
                    <div className="ml-3 border-l border-lw-border pl-2 my-1">
                      <div className="flex justify-between">
                        <span>Residual value ({i.residualValuePercent}%):</span>
                        <span>{Math.round(r.details.residualValue).toLocaleString()} kr</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Principal (price − residual):</span>
                        <span>{Math.round(r.details.principal).toLocaleString()} kr</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Interest on residual (monthly):</span>
                        <span>
                          {Math.round(r.details.monthlyInterestOnRV).toLocaleString()} kr
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Annuity on principal (monthly):</span>
                        <span>
                          {Math.round(r.details.monthlyAnnuity).toLocaleString()} kr
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span>Extra costs:</span>
                    <span>
                      {Math.round(settings.value.monthlyExtraCosts).toLocaleString()} kr
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      Social costs on benefit ({(SOCIAL_COSTS_RATE * 100).toFixed(2)}%):
                    </span>
                    <span>
                      {Math.round(r.details.socialCostsOnBenefit).toLocaleString()} kr
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Fuel compensation:</span>
                    <span>
                      {Math.round(r.totalFuelCompensation).toLocaleString()} kr
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold text-lw-text border-t border-lw-border mt-1 pt-1">
                    <span>= Total company cost:</span>
                    <span>{Math.round(r.totalCompanyCost).toLocaleString()} kr</span>
                  </div>
                </div>

                <div>
                  <div className="font-semibold mb-1 text-lw-text">
                    Deduction calculation
                  </div>
                  <div className="flex justify-between">
                    <span>Total company cost:</span>
                    <span>{Math.round(r.totalCompanyCost).toLocaleString()} kr</span>
                  </div>
                  {settings.value.promotionDeduction !== 0 && (
                    <div className="flex justify-between">
                      <span>− Promotion deduction:</span>
                      <span>
                        {Math.round(settings.value.promotionDeduction).toLocaleString()} kr
                      </span>
                    </div>
                  )}
                  {i.workDrivingKm > 0 && (
                    <div className="flex justify-between">
                      <span>− Work driving allowance ({i.workDrivingKm} km):</span>
                      <span>
                        {Math.round(r.details.workDrivingAllowance).toLocaleString()} kr
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>
                      ÷ (1 + {(SOCIAL_COSTS_RATE * 100).toFixed(2)}% social costs on deduction):
                    </span>
                    <span>{(1 + SOCIAL_COSTS_RATE).toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-lw-text border-t border-lw-border mt-1 pt-1">
                    <span>= Gross salary deduction:</span>
                    <span>{Math.round(r.grossSalaryDeduction).toLocaleString()} kr</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-lw-blue/10 border border-lw-blue/40 text-lw-navy rounded-lg p-4">
          <h5 className="text-sm font-bold mb-2">
            Private Car Comparison (Reference)
          </h5>
          <div className="flex justify-between">
            <span>Theoretical Mileage Compensation:</span>
            <span className="font-bold">
              {Math.round(r.privateCarReferenceCompensation).toLocaleString()} kr
            </span>
          </div>
          <hr className="border-lw-blue/30 my-2" />
          <p className="mb-0 text-xs text-lw-text/70">
            Reference value showing what your tax-free reimbursement would be if
            driving a private car for the same work mileage.
          </p>
        </div>
      </div>
    </div>
  );
};
